"use client"

import { motion, useAnimation } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { useEffect } from "react"
import Image from "next/image"
import Link from "next/link"
import { useGetEcosystemGroupsQuery } from "@/services/ecoType/queries"

interface EcosystemSectionProps {
    className?: string;
}

const EcosystemSection: React.FC<EcosystemSectionProps> = ({ className }) => {
    const controls = useAnimation()
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 })

    // 1. Call API
    const { data: groups = [], isLoading, isError } = useGetEcosystemGroupsQuery()

    useEffect(() => {
        if (inView) {
            controls.start("visible")
        }
    }, [controls, inView])

    // --- Helper tạo Slug  ---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .replace(/\(rvh\)/g, '')
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.6 },
        },
    }

    const childVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    }

    // 2. Fallback
    const fallbackCompanies = [
        { id: "travel", name: "Rovi Travel", logo: "/images/HST/RVTV.png" },
        { id: "hospitality", name: "Rovi Hospitality", logo: "/images/HST/RVHOP.png" },
        { id: "investment", name: "Rovi Investment", logo: "/images/HST/RVINV.png" },
        { id: "logistics", name: "Rovi Logistics", logo: "/images/HST/RVLOG.png" },
    ]

    // 3. Map data (Có tính thêm slug)
    const companyLogos = groups.length > 0 && !isError && !isLoading
        ? groups.map((group) => ({
            id: group.id.toString(),
            name: group.title.replace(" (RVH)", "").trim(),
            logo: group.image || "/images/HST/logo-holding.png",
            slug: createSlug(group.title) // Tạo slug từ title gốc
        }))
        : fallbackCompanies.map(comp => ({
            ...comp,
            slug: createSlug(comp.name) // Tạo slug từ tên fallback
        }))

    return (
        <div ref={ref} className={`w-full relative pt-12 xl:pt-16 2xl:pt-24 min-[1920px]:pt-28 -mb-20 xl:pb-18 2xl:pb-24 min-[1920px]:pb-40 ${className || ''}`}>
            <div className="w-full relative">

                {/* 1. SVG Background for PC*/}
                <svg
                    width="100%"
                    height="100%"
                    className="hidden md:block absolute inset-0 pointer-events-none"
                    style={{ zIndex: 0 }}
                    preserveAspectRatio="none"
                    viewBox="0 0 1200 800"
                >
                    <defs>
                        <mask id="notchMask">
                            <rect width="100%" height="100%" fill="white" />
                            <path d="M 460 0 C 530 0, 550 130, 600 130 C 650 130, 670 0, 740 0 Z" fill="black" />
                        </mask>
                        <linearGradient id="ecoGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#F6F3EC" />
                            <stop offset="100%" stopColor="rgba(246, 243, 236, 0.00)" />
                        </linearGradient>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#ecoGradient)" mask="url(#notchMask)" />
                </svg>

                {/* 2. Image Background for Mobile */}
                <div className="block md:hidden absolute inset-0 z-0">
                    <Image
                        src="/images/ecosystembg.png"
                        alt="Ecosystem Background"
                        fill
                        className="object-cover object-top"
                    />
                </div>

                <div className="w-full max-w-7xl 2xl:max-w-[90rem] min-[1920px]:max-w-[110rem] mx-auto px-6 xl:px-10 2xl:px-16 min-[1920px]:px-32 relative">
                    <motion.div
                        className="relative pt-12 xl:pt-16 2xl:pt-20 min-[1920px]:pt-32 pb-8 xl:pb-12"
                        style={{ zIndex: 1 }}
                        variants={containerVariants}
                        initial="hidden"
                        animate={controls}
                    >
                        {/* LOGO CHÍNH GIỮA */}
                        <div className="absolute top-4 md:top-0 left-1/2 -translate-x-1/2 -translate-y-[65%] z-20">

                            <div className="relative w-32 h-32 md:w-20 md:h-20 xl:w-28 xl:h-28 2xl:w-36 2xl:h-36 min-[1920px]:w-52 min-[1920px]:h-52 rounded-full bg-[#F6F3EC] flex items-center justify-center p-1.5 min-[1920px]:p-3">
                                <Image
                                    src="/images/HST/logo-holding.png"
                                    alt="Rovi Holdings"
                                    width={100}
                                    height={100}
                                    className="object-contain w-full h-full"
                                />
                            </div>
                        </div>

                        {/* Tiêu đề */}
                        <motion.div className="text-center mt-12 2xl:mt-16 min-[1920px]:mt-24" variants={childVariants}>
                            <h3 className="text-[10px] xl:text-xs 2xl:text-sm min-[1920px]:text-lg uppercase tracking-widest font-medium mb-3 2xl:mb-4 min-[1920px]:mb-6"
                                style={{ fontFamily: "'DM Sans', sans-serif", color: "#006633" }}>
                                HỆ SINH THÁI ROVI HOLDINGS
                            </h3>
                            <h2 className="text-2xl xl:text-4xl 2xl:text-5xl min-[1920px]:text-7xl mb-4 2xl:mb-6 min-[1920px]:mb-10 leading-tight"
                                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: "#2D3746", fontWeight: 600, letterSpacing: "-0.02em" }}>
                                Công ty thành viên
                            </h2>
                        </motion.div>

                        <motion.p
                            className="max-w-3xl 2xl:max-w-4xl min-[1920px]:max-w-6xl mx-auto text-center leading-relaxed mb-8 2xl:mb-12 min-[1920px]:mb-20 px-6 text-[15px] xl:text-[15px] 2xl:text-base min-[1920px]:text-xl"
                            variants={childVariants}
                            style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", color: "#333333", lineHeight: "1.6" }}
                        >
                            Hệ sinh thái Rovi Holdings gồm các công ty thành viên hoạt động đa lĩnh vực: du lịch, khách sạn, công nghệ, tài chính và vận tải, hợp tác tạo giá trị bền vững và phát triển lâu dài.
                        </motion.p>

                        <motion.div className="flex justify-center" variants={containerVariants}>

                            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-3 2xl:gap-6 min-[1920px]:gap-10 justify-items-center">
                                {companyLogos.map((company, index) => (
                                    <Link
                                        key={index}
                                        // Update: Sử dụng slug thay vì ID
                                        href={`/ecosystem/${company.slug}`}
                                        className="contents"
                                    >
                                        <motion.div
                                            className="relative bg-white p-1.5 2xl:p-3 min-[1920px]:p-5 rounded-lg 2xl:rounded-xl min-[1920px]:rounded-2xl hover:-translate-y-2 transition-all duration-300 ease-out cursor-pointer flex items-center justify-center overflow-hidden border border-gray-50 hover:border-[#006633] w-full max-w-[170px] xl:max-w-[190px] 2xl:max-w-[240px] min-[1920px]:max-w-[350px]"
                                            variants={childVariants}
                                            whileHover={{ scale: 1.05 }}
                                        >
                                            <div className="w-full h-16 xl:h-20 2xl:h-24 min-[1920px]:h-36 flex items-center justify-center">
                                                <Image
                                                    src={company.logo}
                                                    alt={company.name}
                                                    width={160}
                                                    height={160}
                                                    className="object-contain w-full h-full transition-transform duration-300 hover:scale-110"
                                                />
                                            </div>
                                        </motion.div>
                                    </Link>
                                ))}
                            </div>
                        </motion.div>

                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default EcosystemSection